<script setup lang="ts">
import { RouteRecordName, RouterLink, useRouter } from 'vue-router';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger
} from '@/components/ui/tooltip';
import { canAccessRoute as canAccessAppRoute } from '@/routerAccess';
import { useAccessContextStore } from '@/stores/accessContextStore';
import { useAuthStore } from '@/stores/auth';
import { useConfigCatStore } from '@/stores/configCat';
import { useTenantStore } from '@/stores/tenant';

const props = defineProps<{
    isSidebarOpen: boolean;
}>();

const router = useRouter();
const authStore = useAuthStore();
const accessContextStore = useAccessContextStore();
const tenantStore = useTenantStore();
const configCatStore = useConfigCatStore();

const canShowRoute = (route: any) => {
    if (route.meta.showInSidebar === false) {
        return false;
    }

    // These checks are here and not in router.ts because stores are not available at that point.
    if (route.name === 'Themes' && !configCatStore.slideStudio) {
        return false;
    }
    if (route.name === 'Library' && !configCatStore.slideStudio) {
        return false;
    }
    if (
        route.name === 'Custom Agents' &&
        !configCatStore.isCustomAgentsEnabled
    ) {
        return false;
    }
    if (
        route.name === 'Catalog management' &&
        !configCatStore.isSavedPromptEnabled
    ) {
        return false;
    }
    // Projects only work on the v2 agent.
    if (route.name === 'Projects' && !configCatStore.agentV2) {
        return false;
    }
    return canAccessAppRoute(route, {
        isAuthenticated: authStore.isAuthenticated,
        tenantPermissions: {
            tenantAdmin: tenantStore.tenantAdmin,
            tenantElqano: tenantStore.tenantElqano
        },
        accessEntitlements: {
            loaded: accessContextStore.loaded,
            hasActiveSubscription: accessContextStore.hasActiveSubscription,
            hasCompletedOnboarding: accessContextStore.hasCompletedOnboarding
        }
    });
};

const assignClass = (routeName: RouteRecordName | undefined) => {
    if (routeName === 'Documents') {
        return 'data_sources_button';
    }
    if (routeName === 'Saved prompts') {
        return 'saved_prompts_button';
    }
    if (routeName === 'Themes') {
        return 'themes_button';
    }
    if (routeName === 'Library') {
        return 'library_button';
    }
    return undefined;
};
</script>

<template>
    <div class="menu_top_items font-spartan font-normal text-lg shrink-0">
        <Tooltip
            v-for="route in router.getRoutes()"
            :key="route.name"
            :disabled="props.isSidebarOpen"
        >
            <TooltipTrigger as-child>
                <RouterLink :to="route.path" class="group/menu_item">
                    <div
                        v-if="canShowRoute(route)"
                        :id="assignClass(route.path)"
                        class="flex items-center gap-2 py-2 text-base"
                    >
                        <component
                            :is="route.meta.icon"
                            class="h-5 w-5 shrink-0"
                        />
                        <p
                            class="group-hover/menu_item:underline whitespace-nowrap transition-opacity duration-300"
                            :class="
                                props.isSidebarOpen
                                    ? 'opacity-100'
                                    : 'opacity-0'
                            "
                        >
                            {{ route.name }}
                        </p>
                    </div>
                </RouterLink>
            </TooltipTrigger>
            <TooltipContent side="right">
                <p>{{ route.name }}</p>
            </TooltipContent>
        </Tooltip>
    </div>
</template>
